import { styled } from "@pigment-css/react";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslations, useLocale } from "@acto/web-common/locales";
import { SidePanelContainer } from "../ui";
import { Flex } from "../../primitives/Flex";
import { SectionDivider, CollapsibleSection, Divider } from "../../primitives";
import { CustomerDetailsHeader } from "./ui/CustomerDetailsHeader";
import { CustomerMetricsSection } from "./ui/CustomerMetricsSection";
import { CustomerMetricsSkeleton } from "./ui/CustomerMetricsSkeleton";
import { CustomerInfoSection } from "./ui/CustomerInfoSection";
import { CustomerInfoSkeleton } from "./ui/CustomerInfoSkeleton";
import { CustomerRecapSection } from "./ui/CustomerRecapSection";
import { CustomerOverviewSection } from "./ui/CustomerOverviewSection";
import { CustomerProductsSection } from "./ui/CustomerProductsSection";
import { MonthlyBreakdownList } from "./ui/MonthlyBreakdownList";
import { useCustomerById } from "@acto/taskpane/core/data-access/customers/useCustomerById";
import { useNotes } from "@acto/taskpane/core/data-access/notes/useNotes";
import { useCustomerKpiFigures } from "@acto/taskpane/core/data-access/customers/useCustomerKpiFigures";
import { useComparisonModeContext, useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { KpiComparisonMode } from "@acto/taskpane/core/domain";
import { formatCurrency } from "@acto/taskpane/core/utils/formatters";
import { CustomerDetailsSidePanelSkeleton } from "./CustomerDetailsSidePanelSkeleton";
import { PeriodOption, DateRange } from "../../DateRangeSelector/types";
import { periodOptionToDomainStandardPeriod } from "../../../utils/periodConverters";
import { isComparableWindow, periodToDateRange } from "../../../utils/periodToDateRange";
import { formatComparisonRange } from "../../../utils/dateFormatters";
import { usePeriodLabel } from "../../DateRangeSelector/DateRangeSelector";
import { type CustomerMetric, METRIC_KPI_TYPE, formatMetricWithoutRatio } from "./customerMetric";
import { CustomerMetricSwitch } from "./ui/CustomerMetricSwitch";
import type { ProductGroupRef } from "../ProductDrilldownSidePanel/productDrill";

interface CustomerDetailsSidePanelProps {
  clientId: string;
  selectedPeriod?: PeriodOption;
  selectedCustomDateRange?: DateRange;
  onRecapCountClick: () => void;
  onProductGroupOpen: (group: ProductGroupRef, metric: CustomerMetric) => void;
  onEmployeeClick?: (employeeId: string, employeeName: string) => void;
}

const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[24],
}));

export const CustomerDetailsSidePanel = ({
  clientId,
  selectedPeriod = PeriodOption.LAST_12_MONTHS,
  selectedCustomDateRange,
  onRecapCountClick,
  onProductGroupOpen,
  onEmployeeClick,
}: CustomerDetailsSidePanelProps) => {
  const t = useTranslations();
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const [metric, setMetric] = useState<CustomerMetric>("revenue");

  const domainPeriod = periodOptionToDomainStandardPeriod(selectedPeriod);
  const effectiveDateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const showTrends = isComparableWindow(selectedPeriod, selectedCustomDateRange);
  const periodLabel = usePeriodLabel(selectedPeriod, selectedCustomDateRange);

  const { data: customer, isPending: customerPending, isFetching: customerFetching } = useCustomerById(clientId);
  const comparisonMode = useComparisonModeContext();

  const { data: filteredRecaps } = useNotes({
    clientId,
    dateRange: effectiveDateRange,
  });

  const figures = useCustomerKpiFigures({
    clientId,
    period: domainPeriod,
    dateRange: effectiveDateRange,
    comparisonMode,
  });
  const revenueKpiData = figures.byKpi.REVENUE;
  const profitKpiData = figures.byKpi.PROFIT;
  // urql keeps the previous variables' data while refetching, i.e. the old date range's series.
  const metricKpiData = figures.isFetching ? undefined : figures.byKpi[METRIC_KPI_TYPE[metric]];

  const headerData = useMemo(() => {
    if (!customer) return null;
    return {
      clientName: customer.name,
      externalReference: customer.externalReference ?? null,
      region: customer.region || "",
      employeeName: customer.assignedEmployee?.name || "",
      employeeId: customer.assignedEmployee?.id ?? null,
    };
  }, [customer]);

  const metricsData = useMemo(() => {
    const comparisonLabel =
      comparisonMode === KpiComparisonMode.PERIOD_OVER_PERIOD ? t("kpi.previousPeriod") : t("kpi.previousYear");
    const comparisonTooltip = (
      data:
        | { previousRange: { startDate: string; endDate: string } | null; previousTotalValue: number | null }
        | undefined,
    ) =>
      data?.previousRange && data.previousTotalValue !== null
        ? `${comparisonLabel} ${formatComparisonRange(data.previousRange, locale)}: ${formatCurrency(data.previousTotalValue, locale, currency)}`
        : undefined;

    return {
      revenue: {
        value: revenueKpiData?.totalValue ?? 0,
        percentageChange: showTrends ? (revenueKpiData?.percentageChange ?? 0) : null,
        changeTooltip: comparisonTooltip(revenueKpiData),
      },
      margin: {
        value: revenueKpiData?.totalValue ? (profitKpiData?.totalValue ?? 0) / revenueKpiData.totalValue : 0,
      },
      profit: {
        value: profitKpiData?.totalValue ?? 0,
        percentageChange: showTrends ? (profitKpiData?.percentageChange ?? 0) : null,
        changeTooltip: comparisonTooltip(profitKpiData),
      },
    };
  }, [revenueKpiData, profitKpiData, showTrends, comparisonMode, t, locale, currency]);

  const infoData = useMemo(() => {
    if (!customer) return null;

    const purchaseFrequency = figures.meanDaysBetweenOrders
      ? t("managementView.customerDetails.purchaseFrequencyDays", { days: Math.round(figures.meanDaysBetweenOrders) })
      : "-";

    return {
      purchaseFrequency,
      industry: customer.industry || "-",
      lastVisit: customer.lastVisitDate || "-",
      lastOrder: customer.lastOrder?.date || "-",
    };
  }, [customer, figures.meanDaysBetweenOrders, t]);

  const recapData = useMemo(() => {
    const recap = filteredRecaps?.[0];
    return {
      date: recap?.createdAt ? dayjs(recap.createdAt).format("DD. MMM YYYY") : "",
      content: recap?.summary || recap?.content || "",
    };
  }, [filteredRecaps]);

  const completedRecapCount = filteredRecaps?.length || 0;
  const hasRecap = Boolean(recapData.content) || completedRecapCount > 0;

  const [isOverviewExpanded, setIsOverviewExpanded] = useState(false);

  const monthlyBreakdown = metricKpiData?.monthlyBreakdown ?? [];
  const hasMonthlyBreakdown = monthlyBreakdown.length > 0;

  // Check if customer data matches the requested clientId
  const isCustomerDataMismatched = customer && customer.id !== clientId;

  // Show loading state:
  // 1. Initial load (customerPending)
  // 2. When switching customers (data is being fetched and current data doesn't match requested clientId)
  // 3. When refetching for the same customer but data is stale (isFetching and mismatched)
  const shouldShowLoading = customerPending || (customerFetching && isCustomerDataMismatched);

  // Show metrics loading when KPI data is being fetched (e.g., when switching timeframes)
  const shouldShowMetricsLoading = figures.isFetching;

  // Show info loading when mean days data is being fetched (purchase frequency depends on it)
  const shouldShowInfoLoading = figures.isFetching;

  if (shouldShowLoading) {
    return <CustomerDetailsSidePanelSkeleton />;
  }

  // Show error state if customer not found
  if (!customer || !headerData || !infoData) {
    return (
      <SidePanelContainer>
        <Flex direction="column" gap={24}>
          <div>{t("managementView.customerDetails.notFound")}</div>
        </Flex>
      </SidePanelContainer>
    );
  }

  return (
    <SidePanelContainer>
      <Flex direction="column" gap={24}>
        <CustomerDetailsHeader
          {...headerData}
          onEmployeeClick={
            onEmployeeClick && headerData.employeeId
              ? () => onEmployeeClick(headerData.employeeId?.toString() ?? "", headerData.employeeName)
              : undefined
          }
        />

        <ContentWrapper>
          {shouldShowMetricsLoading ? <CustomerMetricsSkeleton /> : <CustomerMetricsSection {...metricsData} />}

          {shouldShowInfoLoading ? (
            <CustomerInfoSkeleton />
          ) : (
            <CustomerInfoSection {...infoData} clientTags={customer.clientTags ?? []} />
          )}

          {hasRecap && (
            <CustomerRecapSection
              date={recapData.date}
              content={recapData.content}
              completedRecapCount={completedRecapCount}
              onRecapCountClick={onRecapCountClick}
            />
          )}

          <Flex direction="column" gap={24}>
            <CustomerMetricSwitch value={metric} onChange={setMetric} />
            <Divider />
          </Flex>

          <CustomerOverviewSection
            clientId={clientId}
            period={domainPeriod}
            dateRange={effectiveDateRange}
            showTrends={showTrends}
            metric={metric}
          />
          {hasMonthlyBreakdown && (
            <>
              <CollapsibleSection isExpanded={isOverviewExpanded}>
                <MonthlyBreakdownList
                  breakdown={monthlyBreakdown}
                  formatValue={(value) => formatMetricWithoutRatio(metric, value, locale, currency)}
                />
              </CollapsibleSection>
              <SectionDivider onClick={() => setIsOverviewExpanded((prev) => !prev)} isExpanded={isOverviewExpanded} />
            </>
          )}

          <CustomerProductsSection
            clientId={clientId}
            period={domainPeriod}
            dateRange={effectiveDateRange}
            periodLabel={periodLabel}
            showTrends={showTrends}
            onProductGroupOpen={onProductGroupOpen}
            metric={metric}
          />
        </ContentWrapper>
      </Flex>
    </SidePanelContainer>
  );
};
